/**
 * Email Validation Utility
 * Blocks disposable email domains and enforces common TLD requirements
 */

// Common legitimate top-level domains
const ALLOWED_TLDS = [
  'com', 'org', 'edu', 'gov', 'net' 
  //'mil', 'co', 'io', 'ai', 'app', 'dev',
  // Country codes
  //'us', 'uk', 'ca', 'au', 'de', 'fr', 'jp', 'cn', 'in', 'br'
];

// Known disposable/temporary email domains
// Based on your actual fake signups + common temporary email services
const DISPOSABLE_DOMAINS = [
  // From your fake signups
  'apocaw.com',
  'temp.ly',
  'filipx.com',
  'haotuwu.com',
  'fxzig.com',
  'mailto.plus',
  
  // Common temporary email services
  'tempmail.com',
  'guerrillamail.com',
  'mailinator.com',
  '10minutemail.com',
  'throwaway.email',
  'getnada.com',
  'maildrop.cc',
  'yopmail.com',
  'temp-mail.org',
  'fakeinbox.com',
  'trashmail.com',
  'dispostable.com',
  'throwawaymail.com',
  'tempinbox.com',
  'sharklasers.com',
  'guerrillamail.info',
  'grr.la',
  'guerrillamail.biz',
  'guerrillamail.de',
  'spam4.me',
  'mintemail.com',
  'mytrashmail.com',
  'mailnesia.com',
  'trashmail.net',
  'wegwerfmail.de',
  'trashmail.ws',
  'emailondeck.com',
  'tempr.email',
  'mohmal.com',
  'emailsensei.com',
  'anonymbox.com'
];

// Patterns that suggest fake Gmail accounts (keyboard mashing, random characters)
const SUSPICIOUS_PATTERNS = [
  /^[a-z]{18,}@gmail\.com$/i, // 18+ consecutive letters
  /^[a-z0-9]{20,}@gmail\.com$/i, // 20+ random characters
  /([a-z])\1{4,}/i, // Same letter repeated 5+ times
  /^[bcdfghjklmnpqrstvwxyz]{8,}@/i, // 8+ consonants in a row (no vowels)
];

export interface EmailValidationResult {
  isValid: boolean;
  error?: string;
  isSuspicious?: boolean;
  suspiciousReason?: string;
}

/**
 * Validates email against disposable domains, TLD requirements, and suspicious patterns
 */
export function validateEmail(email: string): EmailValidationResult {
  const trimmedEmail = email.trim().toLowerCase();
  
  // Basic format check
  if (!trimmedEmail || !/\S+@\S+\.\S+/.test(trimmedEmail)) {
    return {
      isValid: false,
      error: 'Please enter a valid email address.'
    };
  }
  
  // Extract domain and TLD
  const emailParts = trimmedEmail.split('@');
  if (emailParts.length !== 2) {
    return {
      isValid: false,
      error: 'Please enter a valid email address.'
    };
  }
  
  const domain = emailParts[1];
  const tldParts = domain.split('.');
  const tld = tldParts[tldParts.length - 1];
  
  // Check TLD whitelist
  if (!ALLOWED_TLDS.includes(tld)) {
    return {
      isValid: false,
      error: `Email addresses with ".${tld}" domains are not supported. Please use a common email provider (Gmail, Outlook, Yahoo, etc.).`
    };
  }
  
  // Check disposable domain blocklist
  if (DISPOSABLE_DOMAINS.includes(domain)) {
    return {
      isValid: false,
      error: 'Temporary or disposable email addresses are not allowed. Please use a permanent email address.'
    };
  }
  
  // Check for suspicious patterns (allow signup but flag)
  for (const pattern of SUSPICIOUS_PATTERNS) {
    if (pattern.test(trimmedEmail)) {
      return {
        isValid: true,
        isSuspicious: true,
        suspiciousReason: 'Unusual email pattern detected'
      };
    }
  }
  
  return {
    isValid: true,
    isSuspicious: false
  };
}

/**
 * Quick check if email domain is disposable (for server-side validation)
 */
export function isDisposableEmail(email: string): boolean {
  const domain = email.trim().toLowerCase().split('@')[1];
  return DISPOSABLE_DOMAINS.includes(domain);
}

/**
 * Check if email has allowed TLD
 */
export function hasAllowedTLD(email: string): boolean {
  const domain = email.trim().toLowerCase().split('@')[1];
  if (!domain) return false;
  
  const tld = domain.split('.').pop();
  return tld ? ALLOWED_TLDS.includes(tld) : false;
}


